// BLOQUE 34 — Dashboard operativo del rol "Jefe de Logística": únicamente el
// listado de retiros pendientes de retiro/entrega, a ancho completo, sin
// montos. Navegación directa al ítem clickeado (Retiro/Entrega en Compras).
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Truck, CheckCircle2, ChevronRight } from "lucide-react";
import { API, useAuth } from "../../contexts/AuthContext";
import { Skeleton } from "../../components/ui/skeleton";
import { ItinerarioWidget } from "../compras/ItinerarioWidget";

export default function JefeLogisticaDashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [retiros, setRetiros] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${API}/dashboard/jefe-logistica`, { withCredentials: true })
      .then(({ data }) => setRetiros(data?.retiros || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const today = new Date().toLocaleDateString("es-PY", { weekday: "long", year: "numeric", month: "long", day: "numeric" });
  const firstName = user?.name?.split(" ")[0] || "Usuario";

  return (
    <div className="p-4 sm:p-6 lg:p-8 space-y-6 max-w-[1440px] mx-auto" data-testid="dashboard-jefe-logistica">
      <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-2 fade-in-up">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 tracking-tight" data-testid="dashboard-greeting">
            Hola, {firstName}
          </h1>
          <p className="text-sm text-gray-400 mt-1 capitalize">{today}</p>
        </div>
      </div>

      <div className="bg-white rounded-2xl border border-gray-100 p-5 sm:p-6 fade-in-up" data-testid="jl-retiros-card">
        <div className="flex items-center justify-between mb-5">
          <div className="flex items-center gap-2">
            <div className="p-1.5 rounded-lg bg-blue-50 border border-blue-100/80">
              <Truck size={15} className="text-[#2E6DA4]" />
            </div>
            <h3 className="font-bold text-gray-900 text-[15px]">Pendientes de Retiro / Entrega</h3>
          </div>
          {retiros.length > 0 && (
            <span className="text-[11px] bg-amber-100 text-amber-700 font-bold px-2 py-0.5 rounded-full">{retiros.length}</span>
          )}
        </div>

        {loading ? (
          <div className="space-y-2">{[1, 2, 3, 4].map(i => <Skeleton key={i} className="h-16 w-full rounded-xl" />)}</div>
        ) : retiros.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-14 text-gray-400">
            <CheckCircle2 size={28} className="text-emerald-300 mb-2" />
            <p className="text-sm font-medium">Sin retiros ni entregas pendientes</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3" data-testid="jl-retiros-list">
            {retiros.map((r) => (
              <button
                key={r._id}
                onClick={() => navigate(`/compras/materiales?tab=retiro&open=${encodeURIComponent(r.number)}`)}
                data-testid={`jl-retiro-row-${r._id}`}
                className="w-full flex items-center justify-between gap-2 p-4 rounded-xl border border-gray-100 hover:border-[#2E6DA4]/30 hover:shadow-sm transition-all text-left"
              >
                <div className="min-w-0">
                  <p className="font-mono text-[12px] font-semibold text-gray-500">{r.number}</p>
                  <p className="text-[14px] font-semibold text-gray-800 truncate mt-0.5">{r.project_name || "—"}</p>
                  <p className="text-[12px] text-gray-400 truncate">{r.provider_name || "—"} · OC {r.oc_number || "—"}</p>
                </div>
                <div className="flex items-center gap-2 flex-shrink-0">
                  <span className={`text-[10px] font-bold px-2 py-1 rounded-lg ${r.es_faltante ? "bg-red-100 text-red-700 animate-pulse" : r.paso_faltante === "retiro" ? "bg-lime-100 text-lime-700" : "bg-orange-100 text-orange-700"}`}>
                    {r.es_faltante ? "FALTANTE" : r.paso_faltante === "retiro" ? "Pend. Retiro" : "Pend. Entrega"}
                  </span>
                  <ChevronRight size={14} className="text-gray-300" />
                </div>
              </button>
            ))}
          </div>
        )}
      </div>

      {/* BLOQUE 55 — Itinerario de la semana (retiros/entregas planificados) */}
      <ItinerarioWidget linkTab="retiro" />
    </div>
  );
}
